// Rodapé fixo "Sua carteira" da sidebar (mockup "Tela Aeros Dark -
// Atendimentos Nova Entrada"): saldo de SMS e de leads com um botão cada
// para recarregar/comprar. Fica fora da lista rolável, colado ao fim do
// drawer. Recolhido, vira dois ícones com tooltip.
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import ButtonBase from "@material-ui/core/ButtonBase";
import SmsOutlinedIcon from "@material-ui/icons/SmsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";
import { i18n } from "../translate/i18n";
import rules from "../rules";
import {
  walletCards,
  walletSocketEvents,
  shouldRefreshOnEvent,
} from "./sidebarWalletRules";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: "none",
    borderTop: `1px solid ${theme.palette.aeros.border}`,
    padding: "14px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: theme.palette.aeros.sidebar,
    boxShadow:
      theme.palette.type === "light"
        ? "0 -10px 20px rgba(15,20,40,0.06)"
        : "0 -10px 20px rgba(0,0,0,0.25)",
  },
  title: {
    display: "flex",
    alignItems: "center",
    padding: "0 4px 2px",
    "& span": {
      flex: 1,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    "& svg": { fontSize: 15, color: theme.palette.aeros.textTertiary },
  },
  card: {
    background: theme.palette.aeros.surface,
    border: `1px solid ${theme.palette.aeros.border}`,
    borderRadius: 12,
    padding: "10px 11px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    "& svg": { fontSize: 15, color: theme.palette.text.secondary },
  },
  cardLabel: {
    flex: 1,
    fontSize: 11.5,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardValue: {
    fontSize: 13.5,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
  },
  cta: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: 6,
    borderRadius: 8,
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: "inherit",
    transition: "filter 150ms ease, transform 150ms ease",
    "& svg": { fontSize: 14 },
    "&:hover": { filter: "brightness(1.08)" },
    "&:active": { transform: "scale(0.98)" },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.light}`,
      outlineOffset: 2,
    },
    // 32px é a altura do mockup (desktop, mouse). Em tela de toque o alvo
    // sobe para 44px sem mudar o visual no desktop.
    "@media (pointer: coarse)": { minHeight: 44 },
  },
  ctaSecondary: {
    background: `${theme.palette.primary.main}1F`,
    color: theme.palette.primary.light,
  },
  ctaPrimary: {
    background: theme.palette.aerosGradient,
    color: "#07080D",
    boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
  },
  // Modo recolhido: um ícone quadrado por card, 44px de alvo.
  collapsedRoot: {
    flex: "none",
    borderTop: `1px solid ${theme.palette.aeros.border}`,
    padding: "10px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    background: theme.palette.aeros.sidebar,
  },
  collapsedBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    color: theme.palette.text.secondary,
    "& svg": { fontSize: 19 },
    "&:hover": { background: theme.palette.aeros.surface2, color: theme.palette.text.primary },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.light}`,
      outlineOffset: -2,
    },
  },
}));

const CARD_ICONS = { sms: SmsOutlinedIcon, leads: StorageOutlinedIcon };
const CTA_ICONS = { sms: AddRoundedIcon, leads: ShoppingCartOutlinedIcon };

const canSeeSmsCard = (profile) => {
  const perms = rules[profile];
  return !!(perms && perms.static && perms.static.includes("dashboard:view"));
};

const SidebarWallet = ({ collapsed }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [smsSaldo, setSmsSaldo] = useState(null);
  const [leadsSaldo, setLeadsSaldo] = useState(null);

  const canSeeSms = canSeeSmsCard(user?.profile);
  const companyId = user?.companyId;

  // Falha de saldo não vira toast: a sidebar está em toda tela e um erro
  // aqui repetiria o aviso a cada navegação. O card mostra "—".
  const loadSms = useCallback(async () => {
    if (!canSeeSms) return;
    try {
      const { data } = await api.get("/sms/saldo");
      setSmsSaldo(data?.saldo ?? null);
    } catch (err) {
      setSmsSaldo(null);
    }
  }, [canSeeSms]);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/leads/saldo");
      setLeadsSaldo(data?.disponivel ?? null);
    } catch (err) {
      setLeadsSaldo(null);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    loadSms();
    loadLeads();
  }, [user?.id, loadSms, loadLeads]);

  useEffect(() => {
    if (!socket || typeof socket.on !== "function" || !companyId) return undefined;
    const events = walletSocketEvents(companyId);
    const handlers = [];
    const bind = (name, reload) => {
      const handler = (data) => {
        if (shouldRefreshOnEvent(name, data)) reload();
      };
      socket.on(name, handler);
      handlers.push([name, handler]);
    };
    if (canSeeSms) events.sms.forEach((name) => bind(name, loadSms));
    events.leads.forEach((name) => bind(name, loadLeads));
    return () => {
      handlers.forEach(([name, handler]) => socket.off(name, handler));
    };
  }, [socket, companyId, canSeeSms, loadSms, loadLeads]);

  const cards = walletCards({ smsSaldo, leadsSaldo, canSeeSms });

  if (collapsed) {
    return (
      <div className={classes.collapsedRoot} aria-label={i18n.t("mainDrawer.wallet.title")}>
        {cards.map((card) => {
          const Icon = CARD_ICONS[card.key];
          return (
            <Tooltip key={card.key} title={`${card.label}: ${card.value}`} placement="right">
              <ButtonBase
                component={RouterLink}
                to={card.to}
                className={classes.collapsedBtn}
                aria-label={`${card.label}: ${card.value}`}
              >
                <Icon />
              </ButtonBase>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <span>{i18n.t("mainDrawer.wallet.title")}</span>
        <CreditCardOutlinedIcon aria-hidden="true" />
      </div>
      {cards.map((card) => {
        const Icon = CARD_ICONS[card.key];
        const CtaIcon = CTA_ICONS[card.key];
        return (
          <div key={card.key} className={classes.card}>
            <div className={classes.cardHead}>
              <Icon aria-hidden="true" />
              <span className={classes.cardLabel}>{card.label}</span>
              <span className={classes.cardValue} aria-live="polite">
                {card.value}
              </span>
            </div>
            <ButtonBase
              component={RouterLink}
              to={card.to}
              className={`${classes.cta} ${card.primary ? classes.ctaPrimary : classes.ctaSecondary}`}
              aria-label={`${card.cta} — ${card.label}`}
            >
              <CtaIcon aria-hidden="true" />
              {card.cta}
            </ButtonBase>
          </div>
        );
      })}
    </div>
  );
};

export default SidebarWallet;
